import { createContext, useCallback, useContext } from 'react'
import type { ReactNode } from 'react'
import type { MeasureKind } from '../api/types'

/**
 * Developer mode, as the rest of the interface sees it.
 *
 * WHY THIS IS ITS OWN FILE, AND IT IS NOT TIDINESS. `ui.tsx` and `DataGrid.tsx`
 * are the two places a header or a tile label is actually rendered, so both need
 * to offer an explanation. But `MeasurePanel.tsx` — where the panel lives —
 * imports `EmptyState` and `Loading` FROM `ui.tsx`. Putting the hook there and
 * importing it into ui would close a cycle: ui to panel to ui. Module cycles in
 * this bundler resolve to `undefined` at the point of use rather than failing
 * loudly at build time, which surfaces as a blank component nobody can explain.
 *
 * So the context and the affordance live here, in a module that imports nothing
 * but React and a type. Everything can depend on it; it depends on nothing.
 * MeasurePanel supplies the provider and the panel on top.
 */

export interface DevModeValue {
  /** Which report's documentation this subtree is reading. */
  report: string
  /** (surface/key) -> kind, for everything documented. Empty when the switch is off. */
  refs: Map<string, MeasureKind>
  /** What the page asked the API for, so the panel can say what produced this screen. */
  request: Record<string, unknown>
  open: (surface: string, key: string, extra?: Record<string, unknown>) => void
}

export const DevModeContext = createContext<DevModeValue | null>(null)

/**
 * Ask whether a thing can be explained, and open its panel.
 *
 * Safe outside a provider: `can` is then always false and `open` does nothing,
 * so a shared component like DataGrid can call this unconditionally without
 * every one of its call sites having to opt in.
 */
export function useExplain() {
  const ctx = useContext(DevModeContext)
  const can = useCallback(
    (surface: string | undefined, key: string) =>
      Boolean(ctx && surface && ctx.refs.has(`${surface}/${key}`)),
    [ctx],
  )
  const open = useCallback(
    (surface: string | undefined, key: string, extra?: Record<string, unknown>) => {
      if (ctx && surface) ctx.open(surface, key, extra)
    },
    [ctx],
  )
  return { can, open }
}

/**
 * A label that explains itself when there is something to explain.
 *
 * ===========================================================================
 * ONE AFFORDANCE, AND IT IS THE QUESTION MARK — the owner's call, 20 Sep 2026
 *
 * There were two. This component made the label itself a button with a dotted
 * underline; `ExplainMark` below put a small circled question mark BESIDE a
 * label that was already a button, because a button inside a button is invalid
 * HTML. So whether a documented figure announced itself with an underline or
 * with a mark came down to an implementation detail of the cell it happened to
 * live in — the Sales Explorer's headers were underlined, the invoice ledger's
 * carried marks, and nothing about the figures differed at all.
 *
 * Two affordances for one action is one too many: a reader has to learn both,
 * and learn that they mean the same thing. The mark is the one that survives,
 * for a reason beyond consistency — it works everywhere. An underline cannot
 * be offered on a label that is already a button, and EVERY column header is
 * about to become exactly that, since all of them now sort.
 *
 * So this renders the label untouched and puts the mark after it. The text
 * never moves and never restyles; only the affordance beside it appears.
 *
 * When nothing is documented it renders its children and nothing else — no
 * mark, no click. That is what the manifest buys: a label offers a click only
 * when the click leads somewhere. Before it existed the only options were a
 * header that usually opened "nothing documented for this column" — which
 * reads as a fault — or a hard-coded list of documented keys in the page,
 * which is the same list written twice.
 */
export function Explain({
  surface,
  measureKey,
  extra,
  className = '',
  children,
}: {
  surface?: string
  measureKey: string
  /** Anything about THIS control worth adding to the request summary. */
  extra?: Record<string, unknown>
  className?: string
  children: ReactNode
}) {
  const { can } = useExplain()
  // THE CLASS IS APPLIED IN BOTH BRANCHES. It carries the caller's typography
  // -- `eyebrow` on a grid header, the tile's label styling on a KPI -- so
  // dropping it when nothing is documented would restyle the label instead of
  // merely making it inert, and a report would look different to the person
  // with the switch on. Only the affordance changes; the text never moves.
  if (!can(surface, measureKey)) return <span className={className}>{children}</span>
  return (
    <span className={className}>
      {children}
      <ExplainMark
        surface={surface}
        measureKey={measureKey}
        extra={extra}
        // The tooltip names WHICH label it will explain. Most children are a
        // plain string; where they are not, the key is a truer name than any
        // guess at flattening the nodes.
        label={typeof children === 'string' ? children : measureKey}
      />
    </span>
  )
}

/**
 * THE affordance: a small question mark beside a label, never on it.
 *
 * It began as the special case — for a header that was ALREADY a button,
 * because a button inside a button is invalid HTML that browsers silently
 * repair by moving the inner one out. Stealing the click was no better:
 * sorting is what a reader came to that header for, and a column that stopped
 * sorting the day somebody documented it would be a strange bug to have to
 * explain.
 *
 * As of 20 Sep 2026 it is the only case. `Explain` renders this rather than
 * underlining its own label, so every documented figure on the platform
 * announces itself the same way — see the reasoning there. The mark sits as a
 * SIBLING of the text, present only when there is something behind it and the
 * reader has asked to see it, and it stops the click from reaching whatever
 * the label does.
 */
export function ExplainMark({
  surface,
  measureKey,
  label,
  extra,
}: {
  surface?: string
  measureKey: string
  /** Named in the tooltip, so the mark says WHICH column it will explain. */
  label: string
  extra?: Record<string, unknown>
}) {
  const { can, open } = useExplain()
  if (!can(surface, measureKey)) return null
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        open(surface, measureKey, extra)
      }}
      title={`What does "${label}" mean?`}
      aria-label={`What does "${label}" mean?`}
      className="ml-1 cursor-help align-super text-(length:--text-micro) text-(--color-ink-4) hover:text-(--color-accent)"
    >
      ?
    </button>
  )
}
